// =============================================================================
// lib/api.ts — HTTP client canônico do frontend.
//
// Responsabilidades:
//   - Base URL via env (VITE_API_URL, default http://localhost:3333)
//   - credentials: 'include' em toda requisição (cookies httpOnly)
//   - Interceptor 401 → tenta POST /api/auth/refresh 1× → retry original
//   - Se refresh falhar → limpa store + redireciona /login
//   - CSRF: lê cookie csrf_token (não-httpOnly) → envia em X-CSRF-Token
//     em mutações (POST, PUT, PATCH, DELETE)
//   - Nunca loga payload sensível (LGPD doc 17)
//
// NOTA: Este arquivo é o único ponto de acesso à rede. Nunca useEffect+fetch
//       em componentes — sempre TanStack Query chamando funções deste módulo.
// =============================================================================

import type { LoginBody, LoginResponse, RefreshResponse } from '@elemento/shared-schemas';

import { useAuthStore } from './auth-store';

// URL base — sem trailing slash.
// Default 3333 alinhado ao API_PORT do backend Fastify (apps/api/src/server.ts).
// O fallback so e usado quando VITE_API_URL nao for injetado (dev server iniciado
// antes do envDir ser configurado em apps/web/vite.config.ts).
const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:3333';

// ─── Erros tipados ────────────────────────────────────────────────────────────

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  /**
   * Corpo de erro estruturado que o backend anexa em `body.details`
   * (ver `apps/api/src/shared/errors.ts` — `AppError.details`, e
   * `apps/api/src/app.ts` — error handler).
   *
   * Formato livre por convenção do domínio que lançou o erro (ex.:
   * `{ code: 'FIRST_DUE_DATE_TOO_SOON', ... }` em `modules/simulations/service.ts`)
   * — por isso `unknown`, não um shape fixo. Chamadores devem validar antes
   * de confiar em qualquer campo (é corpo de resposta de rede).
   *
   * **Ausente** (não `undefined` explícito, não objeto vazio) quando o corpo
   * de erro não trouxe `details` — para que `'details' in err` funcione como
   * verificação de presença.
   */
  public readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (details !== undefined) {
      this.details = details;
    }
  }
}

// ─── CSRF helper ─────────────────────────────────────────────────────────────

/**
 * Lê o csrf_token do cookie (não-httpOnly por design do backend).
 * Retorna string vazia se ausente (ex: antes do login).
 */
function getCsrfToken(): string {
  const match = document.cookie.split('; ').find((row) => row.startsWith('csrf_token='));
  return match ? (match.split('=')[1] ?? '') : '';
}

// ─── Métodos que exigem CSRF ──────────────────────────────────────────────────

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// ─── Flag anti-loop de refresh ────────────────────────────────────────────────

let _refreshing: Promise<RefreshResponse> | null = null;

// ─── Refresh interno ─────────────────────────────────────────────────────────

/**
 * Chama POST /api/auth/refresh e retorna a resposta completa (access_token + user).
 * Deduplica chamadas simultâneas: se já houver um refresh em curso,
 * espera o mesmo promise (sem múltiplas requisições em paralelo).
 *
 * Usado em 2 lugares:
 *   1. Interceptor 401 — recupera access token e atualiza user no store.
 *   2. Bootstrap da app — chamado no mount inicial para restaurar sessão
 *      sem precisar re-login (refresh cookie sobrevive ao reload).
 */
async function doRefresh(): Promise<RefreshResponse> {
  if (_refreshing) return _refreshing;

  _refreshing = fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    credentials: 'include',
    body: JSON.stringify({}),
  })
    .then(async (res): Promise<RefreshResponse> => {
      if (!res.ok) {
        throw new ApiError(res.status, 'REFRESH_FAILED', 'Refresh token inválido ou expirado');
      }
      return (await res.json()) as RefreshResponse;
    })
    .finally(() => {
      _refreshing = null;
    });

  return _refreshing;
}

/**
 * Tenta restaurar a sessão a partir do refresh-cookie persistido no browser.
 * - Sucesso: popula o store (user + access_token) e resolve `true`.
 * - Falha: limpa o store e resolve `false` (sem redirect — caller decide).
 *
 * Idempotente. Chamado uma vez no boot da app (SessionBootstrap).
 */
export async function bootstrapSession(): Promise<boolean> {
  try {
    const data = await doRefresh();
    useAuthStore.getState().setAuth(
      {
        id: data.user.id,
        email: data.user.email,
        fullName: data.user.full_name,
        organizationId: data.user.organization_id,
        // Backend (refresh) retorna permissions consolidadas — usa para hidratar
        // o store após reload do SPA sem exigir re-login.
        permissions: data.user.permissions ?? [],
        // Ressincroniza city scope no bootstrap pós-reload (F23-S12).
        cityScopeIds: data.user.city_scope_ids ?? null,
      },
      data.access_token,
    );
    return true;
  } catch {
    useAuthStore.getState().clear();
    return false;
  }
}

// ─── Core fetch ──────────────────────────────────────────────────────────────

interface FetchOptions extends RequestInit {
  /** Não tenta refresh em 401 — flag interna anti-loop */
  _isRetry?: boolean | undefined;
}

async function throwFromResponse(res: Response): Promise<never> {
  let code = 'HTTP_ERROR';
  let message = `Erro ${res.status}`;
  let details: unknown;
  try {
    // Corpo de erro é resposta de rede — valida o formato antes de confiar.
    // `body.details` pode vir com qualquer coisa dentro (ver AppError.details).
    const parsed: unknown = await res.json();
    if (parsed !== null && typeof parsed === 'object') {
      const body = parsed as {
        code?: unknown;
        message?: unknown;
        error?: unknown;
        details?: unknown;
      };
      if (typeof body.code === 'string') code = body.code;
      if (typeof body.message === 'string') message = body.message;
      else if (typeof body.error === 'string') message = body.error;
      if ('details' in body && body.details !== undefined) {
        details = body.details;
      }
    }
  } catch {
    // body não era JSON
  }
  throw new ApiError(res.status, code, message, details);
}

/**
 * Wrapper principal. Injeta:
 * - baseURL
 * - credentials: include
 * - Content-Type: application/json
 * - X-CSRF-Token em mutações
 * - Authorization: Bearer <accessToken> quando disponível
 * - Interceptor 401 → refresh → retry (1×, sem loop)
 */
async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { _isRetry = false, headers: extraHeaders, ...rest } = opts;
  const method = (rest.method ?? 'GET').toUpperCase();

  const accessToken = useAuthStore.getState().accessToken;

  // `Content-Type: application/json` SÓ quando há corpo.
  //
  // O Fastify rejeita com **400 "Body cannot be empty when content-type is set
  // to 'application/json'"** qualquer request que declare JSON e não mande nada.
  // Como este wrapper setava o header sempre, todo `api.delete()` do sistema
  // respondia 400 — inclusive o de desconectar canal, que é como isto apareceu.
  const headers: Record<string, string> = {
    ...(rest.body !== undefined && rest.body !== null
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(extraHeaders as Record<string, string> | undefined),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (MUTATING_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    method,
    headers,
    credentials: 'include',
  });

  // ── Interceptor 401 ─────────────────────────────────────────────────────
  if (res.status === 401 && !_isRetry) {
    // Tenta refresh isoladamente — se o refresh falhar a sessão expirou.
    // Erros da requisição original APÓS refresh bem-sucedido devem propagar
    // normalmente (403, 404, 409 etc) — não disparar logout.
    let refreshed: RefreshResponse;
    try {
      refreshed = await doRefresh();
    } catch {
      useAuthStore.getState().clear();
      window.location.replace('/login');
      throw new ApiError(401, 'SESSION_EXPIRED', 'Sessão expirada. Faça login novamente.');
    }

    useAuthStore.getState().setAccessToken(refreshed.access_token);
    headers['Authorization'] = `Bearer ${refreshed.access_token}`;
    const retryRes = await fetch(`${API_BASE}${path}`, {
      ...rest,
      method,
      headers,
      credentials: 'include',
    });
    if (!retryRes.ok) {
      await throwFromResponse(retryRes);
    }
    if (retryRes.status === 204) {
      return undefined as unknown as T;
    }
    return retryRes.json() as Promise<T>;
  }

  if (!res.ok) {
    await throwFromResponse(res);
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as unknown as T;
  }

  return res.json() as Promise<T>;
}

// ─── Auth endpoints ───────────────────────────────────────────────────────────

/**
 * POST /api/auth/login
 * LGPD: nunca logar credentials — a função não loga o payload.
 */
export async function apiLogin(credentials: LoginBody): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

/**
 * POST /api/auth/logout
 * Idempotente — não lança se cookie ausente.
 */
export async function apiLogout(): Promise<void> {
  return apiFetch<void>('/api/auth/logout', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ─── AI Console — Decisions ───────────────────────────────────────────────────

/**
 * Namespace para endpoints de decisões do agente de IA (F9-S02).
 * LGPD: respostas chegam mascaradas pelo backend — nunca logar decision/context.
 */
export const aiConsole = {
  decisions: {
    /**
     * Lista de decisões com filtros cursor-based.
     * GET /api/ai-console/decisions?cursor=&limit=&date_from=...
     */
    list: <T>(queryString: string) => apiFetch<T>(`/api/ai-console/decisions${queryString}`),

    /**
     * Timeline cronológica de decisões de uma conversa.
     * GET /api/ai-console/decisions/conversations/:conversationId
     */
    timeline: <T>(conversationId: string) =>
      apiFetch<T>(`/api/ai-console/decisions/conversations/${encodeURIComponent(conversationId)}`),
  },

  playground: {
    /**
     * Executa o playground do agente de IA em modo DRY-RUN (F9-S04).
     * POST /api/ai-console/playground
     * LGPD: payload.message pode conter PII — nunca logar.
     * Permissão exigida: ai_playground:run (admin-only).
     */
    run: <T>(body: {
      message: string;
      lead_id?: string | null;
      city_id?: string | null;
      use_real_context: boolean;
    }) =>
      apiFetch<T>('/api/ai-console/playground', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
} as const;

// ─── Genérico ─────────────────────────────────────────────────────────────────

export const api = {
  get: <T>(path: string, opts?: FetchOptions) => apiFetch<T>(path, { ...opts, method: 'GET' }),

  post: <T>(path: string, body: unknown, opts?: FetchOptions) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown, opts?: FetchOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body: JSON.stringify(body) }),

  patch: <T>(path: string, body: unknown, opts?: FetchOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body: JSON.stringify(body) }),

  delete: <T>(path: string, opts?: FetchOptions) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
